import {createContext, type Dispatch, type ReactNode, type SetStateAction, useContext, useMemo, useState} from 'react'

export type ItemProps = {
  id?: string
  url?: string
}

export type ItemPosition = {
  isFirst: boolean
  isLast: boolean
  next: ItemProps | null
  previous: ItemProps | null
}

type ItemPositionMap = {[id: string]: ItemPosition}

type IssueNavigationContextType = {
  navigationNeighbours: ItemPositionMap
  setNavigationNeighbours: Dispatch<SetStateAction<ItemPositionMap>>
  selectedItemId: string
  setSelectedItemId: Dispatch<SetStateAction<string>>
  navigationItemPosition: ItemPosition
}

const IssueNavigationContext = createContext<IssueNavigationContextType | null>(null)

type IssueNavigationContextProviderType = {
  children: ReactNode
}

export function IssueNavigationContextProvider({children}: IssueNavigationContextProviderType) {
  const [navigationNeighbours, setNavigationNeighbours] = useState<ItemPositionMap>({})
  const [selectedItemId, setSelectedItemId] = useState<string>('')

  const navigationItemPosition = useMemo(
    () => navigationNeighbours[selectedItemId] ?? ({} as ItemPosition),
    [navigationNeighbours, selectedItemId],
  )

  const contextValue: IssueNavigationContextType = useMemo(() => {
    return {
      navigationNeighbours,
      setNavigationNeighbours,
      selectedItemId,
      setSelectedItemId,
      navigationItemPosition,
    }
  }, [navigationNeighbours, setNavigationNeighbours, selectedItemId, setSelectedItemId, navigationItemPosition])

  return <IssueNavigationContext.Provider value={contextValue}>{children}</IssueNavigationContext.Provider>
}

export const useIssueNavigationContext = () => {
  const context = useContext(IssueNavigationContext)
  if (!context) {
    throw new Error('useIssueNavigationContext must be used within a IssueNavigationContextProvider.')
  }

  return context
}

try{ IssueNavigationContext.displayName ||= 'IssueNavigationContext' } catch {}
try{ IssueNavigationContextProvider.displayName ||= 'IssueNavigationContextProvider' } catch {}